/**
 * Evidence quote panel for the Qualified Lead detail page.
 * Renders the customer's verbatim phrase that drove the qualification, with
 * `client_booking_quote` as a legacy fallback when `evidence_quote` is null.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Quote } from 'lucide-react'

interface Props {
  readonly evidenceQuote: string | null
  readonly clientBookingQuote: string | null
  readonly contactName: string | null
  readonly capturedAt: string
}

export function EvidenceQuotePanel({
  evidenceQuote,
  clientBookingQuote,
  contactName,
  capturedAt,
}: Props) {
  // evidence_quote is the new column; client_booking_quote was the legacy
  // name that pre-dated the evidence-quote rename. Fall back so older rows
  // still render something useful.
  const quote = evidenceQuote ?? clientBookingQuote

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Quote className="h-4 w-4" />
          Evidence quote
        </CardTitle>
      </CardHeader>
      <CardContent>
        {quote ? (
          <>
            <blockquote className="border-l-4 border-primary/40 pl-4 text-base italic text-foreground">
              {quote}
            </blockquote>
            <p className="mt-3 text-xs text-muted-foreground">
              — {contactName ?? 'Contact'},{' '}
              <time dateTime={capturedAt} title={new Date(capturedAt).toLocaleString()}>
                {new Date(capturedAt).toLocaleString()}
              </time>
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            No evidence quote captured for this qualification.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
